import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState("/cass-logo-group.png");
  useEffect(() => { api.get("/branding").then((r) => r.data.logo_url && setLogoUrl(r.data.logo_url)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next && next.startsWith("/") ? next : "/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#0B1A2F] p-12 text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#2EA3FD]/20 blur-3xl" />
        <div className="absolute -left-16 bottom-10 h-72 w-72 rounded-full bg-[#2EA3FD]/10 blur-3xl" />
        <img src={logoUrl} alt="Company logo" className="h-14 w-auto max-w-[240px] self-start object-contain relative bg-white rounded-lg px-4 py-2.5" />
        <div className="relative">
          <h2 className="text-4xl font-extrabold font-display leading-tight">CASS HR<br />Management System</h2>
          <p className="mt-4 text-slate-300 max-w-md">Recruitment, employee records, leave, performance, reward and reporting — for CASS staff only.</p>
        </div>
        <p className="relative text-xs text-slate-400">Cloud Accounting Support Services Ltd · Authorised users only</p>
      </div>
      <div className="flex-1 grid place-items-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-sm">
          <img src={logoUrl} alt="Company logo" className="h-12 w-auto object-contain mx-auto mb-8 lg:hidden" />
          <h1 className="text-2xl font-extrabold text-[#0B1A2F] font-display">Sign in</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Access is restricted to authorised CASS users.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)} placeholder="you@cass-online.co.uk" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)} className="mt-1" />
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full bg-[#2EA3FD] hover:bg-[#1B8EE6] text-white font-semibold">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="text-center mt-4">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#2EA3FD] hover:underline">Forgot password?</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
